/**
 * PlansTicket — Plans/Overlays ticket content for EditorPanel.
 * Extracted from EditorPanel.tsx (refactoring, no behavior changes).
 */

import React, { useState, useRef } from 'react';
import { useProjectStore, useUIStore, useMapStore } from '../../../stores';
import { fitMapToOverlay } from '../../../utils/overlayFit';
import { fallbackPlacementText } from '../../../utils/dxfPlacementText';
import { dxfLayerColor } from '../../map/useDxfLayers';
import { pdfToTransparentPng, centerPlacementBounds } from '../../../utils/planProxy';
import { dispatchPlanBake, companionLink, adoptCompanionOverlay } from '../../../utils/planBakeDispatch';
import { attachDxfToOverlay } from '../../../utils/dxfAttach';
import { snapLabelsToDxf, unsnapLabels, hasSnappedLabels } from '../../../utils/dxfLabelSnap';
import { fixLabelsFromCad, undoCadFixes, hasCadFixes } from '../../../utils/dxfLabelFix';
import { refitDxfRegistration } from '../../../hooks/useDxfIngestPoller';
import PdfPageSelector from '../PdfPageSelector';
import { uploadAsset } from '../../../services/assetApi';
import { requestVectorIngest, PLAN_BAKE_DEFAULT_DPI } from '../../../services/tileApi';
import { getBakeState, isPlanOverlay, bakeStatusLabel } from '../../../utils/planBake';
import type { Overlay } from '../../../stores/projectStore';

export default function PlansTicket() {
  const overlays = useProjectStore((s) => s.data.overlays);
  const activeOverlayId = useMapStore((s) => s.activeOverlayId);
  const overlayAdjustMode = useMapStore((s) => s.overlayAdjustMode);
  const addNotification = useUIStore((s) => s.addNotification);
  const siteId = useProjectStore((s) => s.meta.siteId);

  // Local state
  const [whiteThreshold, setWhiteThreshold] = useState(245);
  const [overlayProgress, setOverlayProgress] = useState<{ step: string; percent: number } | null>(null);
  const [pendingPdf, setPendingPdf] = useState<File | null>(null);
  const [bakingId, setBakingId] = useState<string | null>(null);
  const [dxfAttachingId, setDxfAttachingId] = useState<string | null>(null);
  const [dxfUploadPct, setDxfUploadPct] = useState<number | null>(null);
  const [dxfPickerId, setDxfPickerId] = useState<string | null>(null);
  const dxfAdjusting = useMapStore((s) => s.dxfAdjustOverlayId);
  const dxfZoning = useMapStore((s) => s.dxfZoneOverlayId);
  const [dxfRefittingId, setDxfRefittingId] = useState<string | null>(null);

  // File input ref for overlay uploads
  const overlayInputRef = useRef<HTMLInputElement>(null);
  // Companion-DXF attach (rung 3): its own input, targeted at one overlay.
  const dxfInputRef = useRef<HTMLInputElement>(null);
  const dxfTargetRef = useRef<string | null>(null);
  // Color-override commit debounce: browsers fire the color input on every
  // movement inside the native picker, and each commit is an entity write.
  const dxfColorTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // Bumped on override reset so the (uncontrolled) swatch input remounts and
  // shows the palette color again.
  const dxfSwatchNonceRef = useRef<Map<string, number>>(new Map());

  // ============================================
  // OVERLAY PROCESSING
  // ============================================
  // The PDF→proxy render (and the "why so modest" history) lives in
  // utils/planProxy.ts — shared with the Vector Layout import, which creates
  // the companion plan overlay from the same upload (WP-A convergence).

  const checkIfHasBlackBackground = async (url: string): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0);
        const samples = [
          ctx.getImageData(5, 5, 1, 1).data,
          ctx.getImageData(img.width - 5, 5, 1, 1).data,
          ctx.getImageData(5, img.height - 5, 1, 1).data,
          ctx.getImageData(img.width - 5, img.height - 5, 1, 1).data,
        ];
        const blackCount = samples.filter((d) => d[0] < 20 && d[1] < 20 && d[2] < 20).length;
        canvas.width = 0; canvas.height = 0; // release GPU memory
        resolve(blackCount >= 3);
      };
      img.onerror = () => resolve(false);
      img.src = url;
    });
  };

  const makeBlackTransparent = async (url: string): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const px = imageData.data;
        for (let i = 0; i < px.length; i += 4) {
          if (px[i] < 10 && px[i + 1] < 10 && px[i + 2] < 10) px[i + 3] = 0;
        }
        ctx.putImageData(imageData, 0, 0);
        canvas.toBlob((blob) => { canvas.width = 0; canvas.height = 0; resolve(blob ? URL.createObjectURL(blob) : url); }, 'image/png');
      };
      img.onerror = () => resolve(url);
      img.src = url;
    });
  };

  const getPdfPageCount = async (file: File): Promise<number> => {
    try {
      const pdfjsLib = await import('pdfjs-dist/legacy/build/pdf.mjs');
      pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/legacy/build/pdf.worker.min.mjs', import.meta.url).toString();
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      return pdf.numPages;
    } catch { return 0; }
  };

  const processOverlayFile = async (file: File, pageNumber?: number) => {
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const isImage = /\.(png|jpg|jpeg)$/i.test(file.name) || /^image\//i.test(file.type);
    if (!isPdf && !isImage) {
      addNotification({ type: 'error', title: 'Invalid file', message: 'Please select a PNG, JPG, or PDF file' });
      return;
    }
    // `pageNumber === undefined` means no page was chosen yet: detect
    // multi-page PDFs and open the selector. An EXPLICIT 1 from the selector
    // must skip detection — routing it back through here re-opened the
    // selector forever, making page 1 of a multi-page PDF impossible to
    // import.
    if (isPdf && pageNumber === undefined) {
      const numPages = await getPdfPageCount(file);
      if (numPages > 1) { setPendingPdf(file); return; }
    }
    const page = pageNumber ?? 1;
    addNotification({ type: 'info', title: 'Processing overlay...', message: file.name });
    setOverlayProgress({ step: 'Loading file...', percent: 10 });
    try {
      let imageUrl: string;
      // Plan-bake fields, populated for PDFs only.
      let bake: Overlay['bake'];
      let sourceR2Key: string | undefined;
      if (isPdf) {
        setOverlayProgress({ step: 'Rendering PDF...', percent: 30 });
        const rendered = await pdfToTransparentPng(file, page, whiteThreshold);
        imageUrl = rendered.url;
        bake = {
          pageIndex: page - 1,
          pageWidthPt: rendered.pageWidthPt,
          pageHeightPt: rendered.pageHeightPt,
          dpi: PLAN_BAKE_DEFAULT_DPI,
          whiteThreshold,
        };
        // Keep the PDF itself: it is the only thing a full-resolution bake can
        // render from, and (unlike the proxy PNG) it stays authoritative for
        // every future re-bake. Uploaded here rather than staged for "Save to
        // Server" because the bake is a server-side job that needs the file to
        // already exist. Failure is non-fatal — the overlay still works as a
        // proxy image, just without the Bake action.
        if (siteId) {
          try {
            setOverlayProgress({ step: 'Uploading source PDF...', percent: 50 });
            const uploaded = await uploadAsset(file, siteId, 'overlay', file.name);
            sourceR2Key = uploaded.r2Key;
          } catch (err) {
            console.warn('[Plans] source PDF upload failed; bake unavailable:', err);
          }

          // Persist the PROXY too, immediately — do not wait for "Save to
          // Server". The proxy is this feature's safety net: it is what the map
          // falls back to whenever the baked tiles cannot be trusted (the plan
          // was moved, or a bake failed). A blob: URL dies with the browser
          // session, so a plan that survived only as a blob would render
          // NOTHING in exactly those cases — the layer would simply vanish.
          try {
            setOverlayProgress({ step: 'Uploading preview...', percent: 70 });
            const proxyBlob = await fetch(imageUrl).then((r) => r.blob());
            const proxy = await uploadAsset(
              proxyBlob, siteId, 'overlay', `${file.name.replace(/\.pdf$/i, '')}_preview.png`);
            URL.revokeObjectURL(imageUrl);
            imageUrl = proxy.url;
          } catch (err) {
            // Non-fatal: the plan still works this session, and an explicit
            // save can still persist it later.
            console.warn('[Plans] preview upload failed; falls back to Save to Server:', err);
          }
        }
      } else {
        imageUrl = URL.createObjectURL(file);
        setOverlayProgress({ step: 'Checking background...', percent: 40 });
        const hasBlackBg = await checkIfHasBlackBackground(imageUrl);
        if (hasBlackBg) {
          const makeTransparent = window.confirm('This image appears to have a black background.\nMake it transparent?');
          if (makeTransparent) {
            setOverlayProgress({ step: 'Removing background...', percent: 60 });
            const oldUrl = imageUrl;
            imageUrl = await makeBlackTransparent(imageUrl);
            if (oldUrl !== imageUrl) URL.revokeObjectURL(oldUrl);
          }
        }
      }
      setOverlayProgress({ step: 'Adding to map...', percent: 85 });
      const bounds = centerPlacementBounds();
      const overlayId = `overlay_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
      const overlayName = isPdf ? file.name.replace(/\.pdf$/i, '.png') : file.name;

      // PDFs arrive here with an /api/v2/assets URL (proxy already uploaded
      // above, so the fallback survives a reload); images still carry a blob:
      // URL that "Save to Server" uploads later.
      useProjectStore.getState().addOverlay({ id: overlayId, name: overlayName, url: imageUrl, bounds, opacity: 0.75, visible: true, sourceR2Key, bake });
      useMapStore.getState().setActiveOverlay(overlayId);
      setOverlayProgress({ step: 'Done', percent: 100 });
      setTimeout(() => setOverlayProgress(null), 1500);
      addNotification({ type: 'success', title: 'Overlay added', message: overlayName });

      // Repair the WP-A link when the converged import lost its plan. A ghost
      // link (layout points at an overlay that is not there) blocks placement,
      // auto-bake and DXF re-align silently and permanently, and importing the
      // sheet by hand is exactly what a user does next — so let that gesture
      // re-link. Deliberately NOT done for `state: 'none'`: a layout that
      // never had a companion may belong to an entirely different sheet, and
      // adopting there would move the wrong plan.
      const link = companionLink(useProjectStore.getState().data.siteLayout);
      if (link.state === 'ghost') {
        const outcome = await adoptCompanionOverlay(overlayId);
        addNotification({
          type: outcome === 'linked' ? 'info' : 'success',
          title: 'Re-linked to the layout',
          message: outcome === 'placed-and-baking'
            ? 'Placed from the layout anchors and baking at full resolution.'
            : outcome === 'placed'
              ? 'Placed from the layout anchors.'
              : 'Place the layout anchors and this sheet will position itself.',
        });
      }
    } catch (error) {
      console.error('Overlay processing error:', error);
      setOverlayProgress(null);
      addNotification({ type: 'error', title: 'Failed to load overlay', message: error instanceof Error ? error.message : 'Unknown error' });
    }
  };

  const handlePdfPageSelect = async (pageNumber: number) => {
    const file = pendingPdf;
    setPendingPdf(null);
    if (!file) return;
    await processOverlayFile(file, pageNumber);
  };

  const handleAddOverlay = () => {
    overlayInputRef.current?.click();
  };

  /**
   * Bake a plan to full resolution: the server re-renders the source PDF at
   * high DPI, applies the recorded parameters, warps it onto the overlay's
   * current 4 corners and serves it as tiles.
   *
   * Explicit by design — a bake spins up a container, so it runs when the user
   * says the placement is right, never automatically on a drag.
   *
   * The fingerprint of what is being baked is recorded up front (not on
   * completion): the user may keep editing while the job runs, and staleness
   * must be measured against the inputs the job actually received.
   */
  const handleBake = async (overlay: Overlay) => {
    if (!siteId || !overlay.bake || bakingId) return;
    setBakingId(overlay.id);
    try {
      // The dispatch + durable-stamp choreography lives in
      // utils/planBakeDispatch.ts — shared with the anchor flow's auto-bake.
      const outcome = await dispatchPlanBake(overlay.id);
      if (outcome === 'started') {
        addNotification({
          type: 'info',
          title: 'Baking full resolution',
          message: `${overlay.name} — this takes a few minutes; the plan stays usable meanwhile.`,
        });
      } else if (outcome === 'skipped-unavailable') {
        // The old direct call THREW a descriptive error here; the delegation
        // must not turn that into a button that silently does nothing.
        addNotification({
          type: 'error',
          title: 'Could not start bake',
          message: 'This plan has no PDF stored on the server yet — save the project first.',
        });
      }
    } catch (err) {
      addNotification({
        type: 'error',
        title: 'Could not start bake',
        message: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setBakingId(null);
    }
  };

  /**
   * Attach a companion DXF to a plan (rung 3, R3-A): upload the file, dispatch
   * the server-side parse, stamp overlay.dxf = 'ingesting'. useDxfIngestPoller
   * takes it from there (done.json → manifest summary → 'ready').
   *
   * A re-attach simply overwrites overlay.dxf with the new job — the old
   * ingest outputs stay in R2 under their jobId prefix (small: JSON, not
   * pixels) and stop being referenced.
   */
  const handleDxfSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const targetId = dxfTargetRef.current;
    dxfTargetRef.current = null;
    if (!file || !targetId || !siteId) return;
    if (!/\.(dxf|pdf)$/i.test(file.name)) {
      addNotification({ type: 'error', title: 'Not a DXF or PDF', message: 'Pick a .dxf (client-native) or a .pdf plan — PDFs are vectorized on the server.' });
      return;
    }
    setDxfAttachingId(targetId);
    try {
      // Upload + dispatch + stamp lives in utils/dxfAttach.ts — shared with
      // the import wizard's staged-DXF path.
      await attachDxfToOverlay(targetId, file, setDxfUploadPct);
      addNotification({
        type: 'info',
        title: /\.pdf$/i.test(file.name) ? 'Vectorizing PDF' : 'Reading DXF',
        // "a few seconds" was true for the first sheet we tested and wildly
        // false for a 500MB one; the banner over the map now carries the clock.
        message: `${file.name} — extracting layers. Large sheets take several minutes; the banner above the map tracks it.`,
      });
    } catch (err) {
      addNotification({
        type: 'error',
        title: 'Could not attach DXF',
        message: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setDxfAttachingId(null);
      setDxfUploadPct(null);
    }
  };

  /** Toggle one DXF layer in/out of the rendered set. Persists via the same
   *  entity write as everything else on the overlay (dxf_json). */
  const toggleDxfLayer = (overlay: Overlay, index: number) => {
    const dxf = overlay.dxf;
    if (!dxf) return;
    const picked = dxf.picked ?? [];
    const next = picked.includes(index)
      ? picked.filter((i) => i !== index)
      : [...picked, index];
    useProjectStore.getState().updateOverlay(overlay.id, { dxf: { ...dxf, picked: next } });
  };

  /** Per-layer color override, debounced (the native color picker streams
   *  change events while dragging). null clears back to the drawing colors. */
  const setDxfLayerColor = (overlay: Overlay, index: number, hex: string | null) => {
    const key = `${overlay.id}:${index}`;
    const timers = dxfColorTimersRef.current;
    const pending = timers.get(key);
    if (pending) clearTimeout(pending);
    timers.set(key, setTimeout(() => {
      timers.delete(key);
      const live = useProjectStore.getState().data.overlays.find((o) => o.id === overlay.id);
      const dxf = live?.dxf;
      if (!dxf) return;
      const overrides = { ...(dxf.colorOverrides ?? {}) };
      if (hex) overrides[index] = hex;
      else delete overrides[index];
      useProjectStore.getState().updateOverlay(overlay.id, {
        dxf: { ...dxf, colorOverrides: Object.keys(overrides).length ? overrides : undefined },
      });
    }, 300));
  };

  /** R3-C: snap the layout's labels to their CAD (DXF) positions — the
   *  forensically proven truth where extractor centers are merge-corrupted. */
  const [snappingId, setSnappingId] = useState<string | null>(null);
  const handleSnapLabels = async (overlay: Overlay) => {
    setSnappingId(overlay.id);
    try {
      const stats = await snapLabelsToDxf(overlay);
      if (stats) {
        addNotification({
          type: 'success',
          title: 'Labels snapped to CAD positions',
          message: `${stats.snapped} labels moved (median ${stats.medianMovePt} pt, ${stats.movedOver3pt} over 3 pt). Save to Server to keep.`,
        });
      } else {
        addNotification({
          type: 'warning',
          title: 'Nothing to snap',
          message: 'Needs a ready DXF aligned to a vector layout with matching labels.',
        });
      }
    } catch (err) {
      addNotification({ type: 'error', title: 'Snap failed', message: err instanceof Error ? err.message : 'Unknown error' });
    } finally {
      setSnappingId(null);
    }
  };
  /** R3-C part 2: text corrections from CAD (oracle-confirmed cases only). */
  const [fixingId, setFixingId] = useState<string | null>(null);
  const handleFixLabels = async (overlay: Overlay) => {
    setFixingId(overlay.id);
    try {
      const stats = await fixLabelsFromCad(overlay);
      if (stats) {
        const total = stats.adoptions + stats.splits + stats.additions;
        addNotification({
          type: total ? 'success' : 'info',
          title: total ? 'Labels corrected from CAD' : 'No confirmed corrections',
          message: total
            ? `${stats.adoptions} texts adopted, ${stats.splits} merges split, ${stats.additions} missing labels added`
              + `${stats.skippedAmbiguous ? `, ${stats.skippedAmbiguous} ambiguous skipped` : ''}. Save to Server to keep.`
            : 'Every extractor label already agrees with the CAD (or has no provable reconstruction).',
        });
      } else {
        addNotification({
          type: 'warning',
          title: 'Nothing to fix',
          message: 'Needs a ready DXF aligned to a vector layout.',
        });
      }
    } catch (err) {
      addNotification({ type: 'error', title: 'Fix failed', message: err instanceof Error ? err.message : 'Unknown error' });
    } finally {
      setFixingId(null);
    }
  };
  const handleUndoFixes = (overlay: Overlay) => {
    const reverted = undoCadFixes(overlay);
    addNotification({
      type: reverted ? 'info' : 'warning',
      title: reverted ? 'CAD corrections reverted' : 'Nothing to revert',
      message: reverted ? `${reverted} corrections undone. Save to Server to keep.` : 'No CAD fixes on this layout.',
    });
  };

  const handleUnsnapLabels = (overlay: Overlay) => {
    const restored = unsnapLabels(overlay);
    addNotification({
      type: restored ? 'info' : 'warning',
      title: restored ? 'Extractor positions restored' : 'Nothing to restore',
      message: restored ? `${restored} labels back to their extracted positions. Save to Server to keep.` : 'No snapped labels on this layout.',
    });
  };

  /** Macro adjust: nudge the whole drawing. Rotation/scale step from the
   *  panel; translation comes from dragging on the map (useDxfAdjust). */
  const bumpDxfAdjust = (overlay: Overlay, patch: Partial<{ rotDeg: number; scale: number }>) => {
    const dxf = overlay.dxf;
    if (!dxf) return;
    const cur = dxf.adjust ?? { dx: 0, dy: 0, rotDeg: 0, scale: 1 };
    useProjectStore.getState().updateOverlay(overlay.id, {
      dxf: {
        ...dxf,
        adjust: {
          ...cur,
          rotDeg: cur.rotDeg + (patch.rotDeg ?? 0),
          scale: cur.scale * (patch.scale ?? 1),
        },
      },
    });
  };
  const resetDxfAdjust = (overlay: Overlay) => {
    const dxf = overlay.dxf;
    if (!dxf) return;
    useProjectStore.getState().updateOverlay(overlay.id, { dxf: { ...dxf, adjust: undefined } });
  };
  /** Current manual offset in metres, for the readout. */
  const adjustMetres = (overlay: Overlay): number => {
    const a = overlay.dxf?.adjust;
    const reg = overlay.dxf?.registration;
    if (!a || reg?.kind !== 'layout') return 0;
    const data = useProjectStore.getState().data;
    const layouts = data.siteLayouts ?? (data.siteLayout ? [data.siteLayout] : []);
    const tf = layouts.find((l) => l.id === reg.layoutId)?.transform;
    if (!tf) return 0;
    const mPerPt = Math.hypot(tf.matrix[0], tf.matrix[3]);
    return Math.hypot(a.dx, a.dy) * mPerPt;
  };

  /** Fill opacity for the DXF's HATCH/SOLID polygons — debounced entity
   *  write, same rationale as the color override (sliders stream events). */
  const setDxfFillOpacity = (overlay: Overlay, value: number) => {
    const key = `${overlay.id}:fillop`;
    const timers = dxfColorTimersRef.current;
    const pending = timers.get(key);
    if (pending) clearTimeout(pending);
    timers.set(key, setTimeout(() => {
      timers.delete(key);
      const live = useProjectStore.getState().data.overlays.find((o) => o.id === overlay.id);
      if (!live?.dxf) return;
      useProjectStore.getState().updateOverlay(overlay.id, {
        dxf: { ...live.dxf, fillOpacity: value },
      });
    }, 250));
  };

  /** All / None — "all" means every DRAWABLE layer (text-only ones have
   *  nothing to render and stay out so None→All→None round-trips cleanly). */
  const setAllDxfLayers = (overlay: Overlay, all: boolean) => {
    const dxf = overlay.dxf;
    if (!dxf) return;
    const picked = all
      ? (dxf.layers ?? []).filter((l) => l.lineFeatures + (l.fillFeatures ?? 0) > 0).map((l) => l.index)
      : [];
    useProjectStore.getState().updateOverlay(overlay.id, { dxf: { ...dxf, picked } });
  };

  /** Re-parse the stored DXF on the server (no re-upload — the source file
   *  is still in R2). Exists so a container-side parser improvement (e.g.
   *  the v2 true-center text anchors) reaches an already-attached DXF: new
   *  jobId, poller resolves it, registration re-fits at ready. */
  const handleDxfReread = async (overlay: Overlay) => {
    const dxf = overlay.dxf;
    if (!dxf || !siteId) return;
    setDxfAttachingId(overlay.id);
    try {
      const isPdf = /\.pdf$/i.test(dxf.srcKey);
      const result = await requestVectorIngest(
        siteId, dxf.srcKey, overlay.id,
        isPdf ? (dxf.pageIndex ?? overlay.bake?.pageIndex ?? 0) : undefined,
      );
      useProjectStore.getState().updateOverlay(overlay.id, {
        dxf: {
          ...dxf, jobId: result.jobId, outPrefix: result.outPrefix,
          status: 'ingesting' as const, error: undefined,
          startedAt: new Date().toISOString(),
        },
      });
      useProjectStore.getState().flushPendingEntityWrites().catch(() => {});
      addNotification({
        type: 'info',
        title: 'Re-reading DXF',
        message: `${overlay.name} — large sheets take several minutes; the banner above the map tracks it.`,
      });
    } catch (err) {
      addNotification({ type: 'error', title: 'Could not re-read DXF', message: err instanceof Error ? err.message : 'Unknown error' });
    } finally {
      setDxfAttachingId(null);
    }
  };

  /** R3-D: vectorize the plan's OWN source PDF on the server — the one-click
   *  path that replaces the desktop PDFIMPORT conversion. No upload: the PDF
   *  is already in R2 (sourceR2Key), and the baked page (bake.pageIndex) is
   *  the page parsed, so the linework matches the raster underneath. Stamps
   *  overlay.dxf exactly like an attach; the poller takes it from there. */
  const handleVectorsFromPlanPdf = async (overlay: Overlay) => {
    const srcKey = overlay.sourceR2Key;
    if (!srcKey || !siteId || !/\.pdf$/i.test(srcKey)) return;
    const pageIndex = overlay.bake?.pageIndex ?? 0;
    setDxfAttachingId(overlay.id);
    try {
      const result = await requestVectorIngest(siteId, srcKey, overlay.id, pageIndex);
      const live = useProjectStore.getState().data.overlays.find((o) => o.id === overlay.id);
      if (!live) throw new Error('Plan no longer exists');
      useProjectStore.getState().updateOverlay(overlay.id, {
        dxf: {
          srcKey, pageIndex,
          jobId: result.jobId, outPrefix: result.outPrefix,
          status: 'ingesting' as const,
          startedAt: new Date().toISOString(),
        },
      });
      useProjectStore.getState().flushPendingEntityWrites().catch(() => {});
      addNotification({
        type: 'info',
        title: 'Vectorizing plan PDF',
        message: `${overlay.name} — extracting layers and labels from page ${pageIndex + 1}. Large sheets take several minutes; the banner above the map tracks it.`,
      });
    } catch (err) {
      addNotification({ type: 'error', title: 'Could not vectorize PDF', message: err instanceof Error ? err.message : 'Unknown error' });
    } finally {
      setDxfAttachingId(null);
    }
  };

  /** Re-run the DXF→layout alignment against the layouts that exist NOW —
   *  the rescue for a layout imported (or re-imported) after the ingest. */
  const handleDxfRefit = async (overlay: Overlay) => {
    setDxfRefittingId(overlay.id);
    try {
      const fit = await refitDxfRegistration(overlay);
      if (fit) {
        addNotification({
          type: 'success',
          title: 'DXF aligned to layout',
          message: `${fit.matched} matched labels (±${fit.rmsPt} pt).`,
        });
      } else {
        addNotification({
          type: 'warning',
          title: 'No layout match',
          message: 'No vector layout with placed anchors shares enough unique labels with this DXF. Import and anchor the layout, then try again.',
        });
      }
    } catch (err) {
      addNotification({
        type: 'error',
        title: 'Alignment failed',
        message: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setDxfRefittingId(null);
    }
  };

  const handleOverlaySelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    for (const file of Array.from(files)) await processOverlayFile(file);
    e.target.value = '';
  };

  const handleOverlayDragOver = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.add('orthophoto-dropzone--dragover'); };
  const handleOverlayDragLeave = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.remove('orthophoto-dropzone--dragover'); };
  const handleOverlayDrop = async (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    e.currentTarget.classList.remove('orthophoto-dropzone--dragover');
    for (const file of Array.from(e.dataTransfer.files)) await processOverlayFile(file);
  };

  return (
    <>
      {/* Hidden file input for overlay uploads */}
      <input ref={overlayInputRef} type="file" accept=".png,.jpg,.jpeg,.pdf,application/pdf,image/png,image/jpeg" multiple onChange={handleOverlaySelect} style={{ display: 'none' }} />

      {/* Hidden file input for companion-DXF attach (rung 3) */}
      <input ref={dxfInputRef} type="file" accept=".dxf,.pdf" onChange={handleDxfSelect} style={{ display: 'none' }} />

      {/* PDF Page Selector Modal */}
      {pendingPdf && (
        <PdfPageSelector file={pendingPdf} onSelect={handlePdfPageSelect} onCancel={() => setPendingPdf(null)} />
      )}

      <div className="ep-ticket-content">
        <div className="ep-section-label">Upload Plan</div>
        <div
          className="orthophoto-dropzone"
          onClick={handleAddOverlay}
          onDragOver={handleOverlayDragOver}
          onDragLeave={handleOverlayDragLeave}
          onDrop={handleOverlayDrop}
        >
          <span>Drop PNG / PDF or click</span>
        </div>
        <div className="ep-threshold-row">
          <span className="ep-threshold-label">White removal</span>
          <input
            type="range"
            min="0"
            max="255"
            step="1"
            value={whiteThreshold}
            onChange={(e) => setWhiteThreshold(Number(e.target.value))}
            className="ep-threshold-slider"
          />
          <span className="ep-threshold-val">{whiteThreshold === 0 ? 'Off' : whiteThreshold}</span>
        </div>

        {overlayProgress && (
          <div className="ep-progress">
            <div className="ep-progress__bar">
              <div className="ep-progress__fill" style={{ width: `${overlayProgress.percent}%` }} />
            </div>
            <span className="ep-progress__label">{overlayProgress.step}</span>
          </div>
        )}

        {overlays.length > 0 && (
          <>
            <div className="ep-divider" />
            <div className="ep-section-label">Layers ({overlays.length})</div>
            <div className="overlay-list">
              {overlays.map((overlay) => {
                const bakeState = getBakeState(overlay);
                const bakeLabel = bakeStatusLabel(bakeState);
                const canBake = isPlanOverlay(overlay) && !!overlay.bake && !!siteId
                  && bakeState !== 'baking' && bakeState !== 'baked';
                return (
                <React.Fragment key={overlay.id}>
                <div className={`overlay-item${activeOverlayId === overlay.id ? ' overlay-item--active' : ''}`} onClick={() => {
                  // No-op when already active: setActiveOverlay drops adjust
                  // mode, and re-clicking the row of the overlay being
                  // adjusted must not end the session.
                  if (useMapStore.getState().activeOverlayId !== overlay.id) {
                    useMapStore.getState().setActiveOverlay(overlay.id);
                    // Selecting a plan also brings the map to it (owner,
                    // 2026-09-27) — a sheet can sit far from the site's field.
                    fitMapToOverlay(useMapStore.getState().map, overlay);
                  }
                }}>
                  <label className="overlay-checkbox" onClick={(ev) => ev.stopPropagation()}>
                    <input type="checkbox" checked={overlay.visible} onChange={() => useProjectStore.getState().toggleOverlayVisibility(overlay.id)} />
                    <span>{overlay.name}</span>
                    {bakeLabel && (
                      <span className={`overlay-bake-chip overlay-bake-chip--${bakeState}`}>{bakeLabel}</span>
                    )}
                    {overlay.dxf && (
                      <span
                        className={`overlay-bake-chip overlay-bake-chip--${
                          overlay.dxf.status === 'ready' ? 'baked'
                            : overlay.dxf.status === 'ingesting' ? 'baking' : 'error'
                        }`}
                        title={overlay.dxf.status === 'ready'
                          ? `DXF attached: ${overlay.dxf.layers?.length ?? 0} layers, ${overlay.dxf.textTotal ?? 0} texts`
                          : overlay.dxf.status === 'ingesting'
                            ? 'Reading the DXF on the server…'
                            : overlay.dxf.error ?? 'DXF ingest failed'}
                      >
                        {overlay.dxf.status === 'ready' ? 'DXF ✓'
                          : overlay.dxf.status === 'ingesting' ? 'DXF…' : 'DXF ✗'}
                      </span>
                    )}
                  </label>
                  {/* Same explanation as the Layers panel: this is the OTHER
                      opacity control, and it is the one you are looking at while
                      importing and tuning a plan, so the slider-vs-map mismatch
                      surfaces here first. */}
                  <input type="range" min="0" max="100" step="1" title="Opacity when zoomed in. Plans fade automatically as you zoom out so they don't hide the map." value={Math.round(overlay.opacity * 100)} onClick={(ev) => ev.stopPropagation()} onChange={(e) => useProjectStore.getState().setOverlayOpacity(overlay.id, Number(e.target.value) / 100)} className="overlay-opacity" />
                  <div className="overlay-item__actions">
                    {canBake && (
                      <button
                        type="button"
                        title={bakeState === 'stale-placement'
                          ? 'The plan moved since the last bake — re-bake to sharpen it in its new position'
                          : bakeState === 'stale-erase'
                            ? 'Areas were erased since the last bake — re-bake to apply them at full resolution'
                            : 'Render this PDF at full resolution on the server'}
                        className="overlay-item__bake"
                        disabled={bakingId === overlay.id}
                        onClick={(ev) => { ev.stopPropagation(); handleBake(overlay); }}
                      >
                        {bakeState === 'never' ? 'Bake full resolution' : 'Re-bake'}
                      </button>
                    )}
                    {isPlanOverlay(overlay) && !!siteId && /\.pdf$/i.test(overlay.sourceR2Key ?? '') && (
                      <button
                        type="button"
                        title={overlay.dxf
                          ? "Re-extract the vector layers and labels from this plan's own PDF on the server (replaces the current vector set)"
                          : "Extract the vector layers and labels straight from this plan's PDF on the server — no CAD conversion needed"}
                        disabled={dxfAttachingId === overlay.id}
                        onClick={(ev) => { ev.stopPropagation(); handleVectorsFromPlanPdf(overlay); }}
                      >
                        {dxfAttachingId === overlay.id && dxfUploadPct === null
                          ? 'Starting…'
                          : overlay.dxf ? 'Re-extract from PDF' : 'Vectors from PDF'}
                      </button>
                    )}
                    {isPlanOverlay(overlay) && !!siteId && (
                      <button
                        type="button"
                        title={overlay.dxf
                          ? 'Replace the attached vector source with a new .dxf or .pdf file'
                          : 'Attach a client-native DXF of this plan, or a PDF to vectorize on the server, to unlock per-layer vector rendering'}
                        disabled={dxfAttachingId === overlay.id}
                        onClick={(ev) => {
                          ev.stopPropagation();
                          dxfTargetRef.current = overlay.id;
                          dxfInputRef.current?.click();
                        }}
                      >
                        {dxfAttachingId === overlay.id
                          ? (dxfUploadPct !== null ? `Uploading… ${dxfUploadPct}%` : 'Uploading…')
                          : overlay.dxf ? 'Replace DXF/PDF' : 'Attach DXF/PDF'}
                      </button>
                    )}
                    {overlay.dxf?.status === 'ready' && (
                      <button
                        type="button"
                        title="Pick which DXF layers render on the map"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          setDxfPickerId(dxfPickerId === overlay.id ? null : overlay.id);
                        }}
                      >
                        {dxfPickerId === overlay.id ? 'Hide layers' : `DXF layers (${overlay.dxf.picked?.length ?? 0})`}
                      </button>
                    )}
                    {/* Placement adjust is a deliberate ticket action, like the
                        ortho Adjust button — clicking the sheet on the map only
                        selects it and never flips baked tiles to the preview. */}
                    <button
                      type="button"
                      title="Show move/scale/rotate handles. Baked tiles switch to the preview while adjusting; re-bake when done."
                      onClick={(ev) => {
                        ev.stopPropagation();
                        const adjustingThis = overlayAdjustMode && activeOverlayId === overlay.id;
                        if (adjustingThis) useMapStore.getState().exitOverlayAdjustMode();
                        else useMapStore.getState().enterOverlayAdjustMode(overlay.id);
                      }}
                    >
                      {overlayAdjustMode && activeOverlayId === overlay.id ? 'Done' : 'Adjust'}
                    </button>
                    <button type="button" title="Register on map" onClick={(ev) => { ev.stopPropagation(); useUIStore.getState().setOverlayAnchorMode(overlay.id); }}>Register</button>
                    <button type="button" title="Crop overlay" onClick={(ev) => { ev.stopPropagation(); useMapStore.getState().setActiveOverlay(overlay.id); useMapStore.getState().setOverlayEditMode('crop'); }}>Crop</button>
                    <button type="button" title="Delete area from overlay" onClick={(ev) => { ev.stopPropagation(); useMapStore.getState().setActiveOverlay(overlay.id); useMapStore.getState().setOverlayEditMode('delete'); }}>Erase</button>
                    <button type="button" title="Remove overlay" className="overlay-item__remove" onClick={(ev) => {
                      ev.stopPropagation();
                      if (window.confirm(`Remove overlay "${overlay.name}"?`)) {
                        useProjectStore.getState().removeOverlay(overlay.id);
                        if (activeOverlayId === overlay.id) useMapStore.getState().setActiveOverlay(null);
                        addNotification({ type: 'info', title: 'Overlay removed', message: overlay.name });
                      }
                    }}>&times;</button>
                  </div>
                </div>
                {dxfPickerId === overlay.id && overlay.dxf?.status === 'ready' && (
                  <div className="dxf-picker" onClick={(ev) => ev.stopPropagation()}>
                    <div className="dxf-picker__reg">
                      {(() => {
                        const reg = overlay.dxf.registration;
                        if (reg?.kind !== 'layout') {
                          // Source-aware: exact for a PDF (page box), rough for a DXF.
                          return fallbackPlacementText(overlay.dxf);
                        }
                        // A registration bound to a deleted/replaced layout
                        // silently degrades to the extents stretch — showing
                        // its stale numbers as if current misled the first
                        // real occurrence (Kalia, re-imported layout). Say
                        // the truth instead.
                        const data = useProjectStore.getState().data;
                        const layouts = data.siteLayouts ?? (data.siteLayout ? [data.siteLayout] : []);
                        if (!layouts.some((l) => l.id === reg.layoutId)) {
                          return '⚠ Aligned to a layout that was removed — placement is approximate. Press Re-align to bind to the current layout.';
                        }
                        return `Aligned to the layout via ${reg.matched} matched labels (±${reg.rmsPt} pt)`;
                      })()}
                    </div>
                    {/* Actions row: alignment + data operations. */}
                    <div className="dxf-picker__controls">
                      <button
                        type="button"
                        title="Match this DXF's text against the site's vector layout labels and align the lines through the layout's registration. Use after importing or re-anchoring a layout."
                        disabled={dxfRefittingId === overlay.id}
                        onClick={(ev) => { ev.stopPropagation(); handleDxfRefit(overlay); }}
                      >
                        {dxfRefittingId === overlay.id ? 'Aligning…'
                          : overlay.dxf.registration?.kind === 'layout' ? 'Re-align' : 'Align to layout'}
                      </button>
                      <button
                        type="button"
                        title="Re-parse the stored DXF on the server (no re-upload) — picks up server-side parser improvements, then re-aligns"
                        disabled={dxfAttachingId === overlay.id}
                        onClick={(ev) => { ev.stopPropagation(); handleDxfReread(overlay); }}
                      >
                        Re-read
                      </button>
                      {overlay.dxf.registration?.kind === 'layout' && (
                        <button
                          type="button"
                          title="Replace matched labels' positions with their CAD (DXF) positions — proven ground truth where the PDF extraction is merge-corrupted. Reversible with Unsnap."
                          disabled={snappingId === overlay.id}
                          onClick={(ev) => { ev.stopPropagation(); handleSnapLabels(overlay); }}
                        >
                          {snappingId === overlay.id ? 'Snapping…' : 'Snap labels'}
                        </button>
                      )}
                      {hasSnappedLabels(overlay) && (
                        <button
                          type="button"
                          title="Restore the extracted label positions"
                          onClick={(ev) => { ev.stopPropagation(); handleUnsnapLabels(overlay); }}
                        >
                          Unsnap
                        </button>
                      )}
                      {overlay.dxf.registration?.kind === 'layout' && (
                        <button
                          type="button"
                          title="Correct label TEXT from CAD — only oracle-confirmed cases: adopt the CAD form (lost spaces, truncations), split confirmed merges into their parts, add topology labels the extractor missed. Reversible with Undo fixes."
                          disabled={fixingId === overlay.id}
                          onClick={(ev) => { ev.stopPropagation(); handleFixLabels(overlay); }}
                        >
                          {fixingId === overlay.id ? 'Fixing…' : 'Fix labels'}
                        </button>
                      )}
                      {hasCadFixes(overlay) && (
                        <button
                          type="button"
                          title="Revert every CAD text correction"
                          onClick={(ev) => { ev.stopPropagation(); handleUndoFixes(overlay); }}
                        >
                          Undo fixes
                        </button>
                      )}
                    </div>
                    {/* Macro adjust — the automatic fit gets within ~1 m; the
                        last metre is the user's (see dxfRegistration: a bias
                        identical across all labels is invisible to the fit). */}
                    <div className="dxf-picker__adjust">
                      <button
                        type="button"
                        className={dxfAdjusting === overlay.id ? 'dxf-picker__adjust--on' : ''}
                        title="Drag on the map to move the whole drawing; arrow keys nudge 0.25 m (1 m with Shift); Esc when done."
                        onClick={(ev) => {
                          ev.stopPropagation();
                          useMapStore.getState().setDxfAdjustOverlay(
                            dxfAdjusting === overlay.id ? null : overlay.id);
                        }}
                      >
                        {dxfAdjusting === overlay.id ? 'Done adjusting' : 'Adjust vectors'}
                      </button>
                      {dxfAdjusting === overlay.id && (
                        <>
                          <button type="button" title="Rotate 0.1° counter-clockwise" onClick={(ev) => { ev.stopPropagation(); bumpDxfAdjust(overlay, { rotDeg: 0.1 }); }}>⟲</button>
                          <button type="button" title="Rotate 0.1° clockwise" onClick={(ev) => { ev.stopPropagation(); bumpDxfAdjust(overlay, { rotDeg: -0.1 }); }}>⟳</button>
                          <button type="button" title="Scale up 0.1%" onClick={(ev) => { ev.stopPropagation(); bumpDxfAdjust(overlay, { scale: 1.001 }); }}>＋</button>
                          <button type="button" title="Scale down 0.1%" onClick={(ev) => { ev.stopPropagation(); bumpDxfAdjust(overlay, { scale: 1 / 1.001 }); }}>－</button>
                        </>
                      )}
                      <button
                        type="button"
                        className={dxfZoning === overlay.id ? 'dxf-picker__adjust--on' : ''}
                        title="Local fixes for as-built deviation. Drag LEFT→RIGHT to take only what is fully inside the box, RIGHT→LEFT to take everything it touches (as in CAD). Click one element to grab just that. Then drag the zone or nudge it with the arrow keys (0.25 m, 1 m with Shift). Delete removes the selected zone; Esc exits."
                        onClick={(ev) => {
                          ev.stopPropagation();
                          useMapStore.getState().setDxfZoneOverlay(
                            dxfZoning === overlay.id ? null : overlay.id);
                        }}
                      >
                        {dxfZoning === overlay.id ? 'Done local fixes' : `Local fixes${overlay.dxf.zones?.length ? ` (${overlay.dxf.zones.length})` : ''}`}
                      </button>
                      {overlay.dxf.zones?.length ? (
                        <button
                          type="button"
                          title="Remove every local correction"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            const dxf = overlay.dxf!;
                            useProjectStore.getState().updateOverlay(overlay.id, { dxf: { ...dxf, zones: undefined } });
                            useMapStore.getState().setDxfSelectedZone(null);
                          }}
                        >
                          Clear zones
                        </button>
                      ) : null}
                      {overlay.dxf.adjust && (
                        <>
                          <span className="dxf-picker__adjustval">
                            {adjustMetres(overlay).toFixed(2)} m
                            {overlay.dxf.adjust.rotDeg ? `, ${overlay.dxf.adjust.rotDeg.toFixed(1)}°` : ''}
                            {overlay.dxf.adjust.scale !== 1 ? `, ×${overlay.dxf.adjust.scale.toFixed(4)}` : ''}
                          </span>
                          <button type="button" title="Clear the manual correction" onClick={(ev) => { ev.stopPropagation(); resetDxfAdjust(overlay); }}>Reset</button>
                        </>
                      )}
                    </div>

                    {/* Fills opacity — the drawing's solid areas over the ortho. */}
                    <div className="dxf-picker__fillrow">
                      <span>Fill opacity</span>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        step="5"
                        defaultValue={Math.round((overlay.dxf.fillOpacity ?? 0.35) * 100)}
                        onClick={(ev) => ev.stopPropagation()}
                        onChange={(ev) => setDxfFillOpacity(overlay, Number(ev.target.value) / 100)}
                      />
                    </div>
                    {/* Layer-list header: count + the selection pair. */}
                    <div className="dxf-picker__listhead">
                      <span>Layers ({(overlay.dxf.layers ?? []).length})</span>
                      <button
                        type="button"
                        title="Show every layer that has linework"
                        onClick={(ev) => { ev.stopPropagation(); setAllDxfLayers(overlay, true); }}
                      >
                        All
                      </button>
                      <button
                        type="button"
                        title="Hide all DXF layers"
                        onClick={(ev) => { ev.stopPropagation(); setAllDxfLayers(overlay, false); }}
                      >
                        None
                      </button>
                    </div>
                    {[...(overlay.dxf.layers ?? [])]
                      .sort((a, b) => (b.lineFeatures + (b.fillFeatures ?? 0)) - (a.lineFeatures + (a.fillFeatures ?? 0)))
                      .map((l) => {
                        const drawableCount = l.lineFeatures + (l.fillFeatures ?? 0);
                        const drawable = drawableCount > 0;
                        const checked = overlay.dxf!.picked?.includes(l.index) ?? false;
                        return (
                          <label
                            key={l.index}
                            className={`dxf-picker__row${drawable ? '' : ' dxf-picker__row--empty'}`}
                            title={drawable
                              ? `${l.lineFeatures.toLocaleString()} lines`
                                + `${l.fillFeatures ? `, ${l.fillFeatures.toLocaleString()} fills` : ''}`
                                + `${l.textCount ? `, ${l.textCount} texts` : ''}`
                                + (l.overflow
                                  ? `; ${l.overflow.toLocaleString()} more were not drawn (per-layer cap) — ${(drawableCount + l.overflow).toLocaleString()} in the drawing`
                                  : '')
                              : 'Text-only layer — nothing to draw'}
                          >
                            <input
                              type="checkbox"
                              disabled={!drawable}
                              checked={checked}
                              onChange={() => toggleDxfLayer(overlay, l.index)}
                            />
                            <input
                              type="color"
                              className="dxf-picker__swatch"
                              key={`sw-${l.index}-${dxfSwatchNonceRef.current.get(`${overlay.id}:${l.index}`) ?? 0}`}
                              defaultValue={overlay.dxf!.colorOverrides?.[l.index] ?? dxfLayerColor(l.index)}
                              title="Override this layer's color (the drawing's own colors are used until you pick one)"
                              onClick={(ev) => ev.stopPropagation()}
                              onChange={(ev) => setDxfLayerColor(overlay, l.index, ev.target.value)}
                            />
                            {overlay.dxf!.colorOverrides?.[l.index] && (
                              <button
                                type="button"
                                className="dxf-picker__swatch-reset"
                                title="Back to the drawing's own colors"
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  ev.preventDefault();
                                  const key = `${overlay.id}:${l.index}`;
                                  dxfSwatchNonceRef.current.set(key, (dxfSwatchNonceRef.current.get(key) ?? 0) + 1);
                                  setDxfLayerColor(overlay, l.index, null);
                                }}
                              >
                                ↺
                              </button>
                            )}
                            <span className="dxf-picker__name">{l.name}</span>
                            <span className="dxf-picker__count">
                              {l.overflow
                                ? `${drawableCount.toLocaleString()} of ${(drawableCount + l.overflow).toLocaleString()}`
                                : drawableCount.toLocaleString()}
                            </span>
                          </label>
                        );
                      })}
                  </div>
                )}
                </React.Fragment>
                );
              })}
            </div>
          </>
        )}
        {overlays.length === 0 && <p className="ep-empty-text">No plans loaded</p>}
      </div>
    </>
  );
}
